import { Component, Input, OnInit, ChangeDetectionStrategy, computed, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { concat, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { OnsiteCampaign, OnsiteCampaignType, ReferralTypes } from 'app/campaign/campaign.model';
import { Widget } from 'app/campaign/widgets.model';
import { CampaignService } from 'app/shared/services/campaign.service';
import { CampaignTemplatesService } from 'app/campaign/templates/campaign-templates.service';
import { CampaignTemplate, TemplateStatus } from 'app/campaign/templates/template.model';
import { WidgetService } from 'app/shared/services/widget.service';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { IndeterminateProgressDialogComponent } from 'app/shared/progress/indeterminate-progress-dialog/indeterminate-progress-dialog.component';
import { FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';

@Component({
  selector: 'app-flyout-referral-campaigns',
  templateUrl: './flyout-referral-campaigns.component.html',
  styleUrls: ['./flyout-referral-campaigns.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutReferralCampaignsComponent implements OnInit {
  public referringTemplates: Array<CampaignTemplate>;
  public friendsTemplates: Array<CampaignTemplate>;
  private readonly logger: Logger;
  public loadingWidgetTemplates: boolean;
  public accordionStep = 'nameReferral';
  public ReferralTypes = ReferralTypes;
  private progressDialogRef: MatDialogRef<IndeterminateProgressDialogComponent>;
  public readonly disabledFriends = signal(false);
  private _openingData: FlyoutOpeningData;
  public readonly campaignNameValidationError = signal<string | undefined>(undefined);
  public readonly nameReferral = signal('');
  public readonly referralName = computed(() => this.nameReferral().trim());
  public readonly preselectedFriendTemplateId = signal<number | undefined>(undefined);
  public readonly isReferringPreselected = signal(false);
  // Both halves are created together once the friend is picked, so they sit next to each other in lists.
  public readonly selectedReferringTemplateId = signal<number | undefined>(undefined);
  public readonly selectedFriendTemplateId = signal<number | undefined>(undefined);
  private readonly hasValidName = computed(() => !!this.referralName() && !this.campaignNameValidationError());
  public readonly referringTooltip = computed(() => {
    if (this.isReferringPreselected()) {
      return 'referral-campaign.referring-template-preselected';
    }
    if (!this.hasValidName()) {
      return 'campaign.warningNoName';
    }
    return this.disabledFriends() ? null : 'campaign.warningExistingRef';
  });
  public readonly friendTooltip = computed(() => {
    if (this.preselectedFriendTemplateId()) {
      return 'referral-campaign.friend-template-preselected';
    }
    if (!this.hasValidName()) {
      return 'campaign.warningNoName';
    }
    return this.disabledFriends() && !this.selectedReferringTemplateId() ? 'campaign.warningPriorityRef' : null;
  });
  private creatingPair = false;
  private createdIds: { referrer?: number; group?: number; friend?: number } = {};
  @Input()
  public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this._openingData = openingData;
    const preselected = this._openingData?.preselectedTemplate;
    this.preselectedFriendTemplateId.set(preselected?.type === ReferralTypes.Friends ? preselected.id : undefined);
    this.isReferringPreselected.set(preselected?.type === ReferralTypes.Referring);
    this.selectedReferringTemplateId.set(this.isReferringPreselected() ? preselected.id : undefined);
    this.selectedFriendTemplateId.set(this.preselectedFriendTemplateId());
    this.disabledFriends.set(!this._openingData?.campaignReferral);
    if (this._openingData.subType === ReferralTypes.Friends) {
      this.accordionStep = ReferralTypes.Friends;
      this.nameReferral.set(this._openingData?.campaignReferral?.name ?? '');
    }
  }

  constructor(
    private readonly widgetService: WidgetService,
    public readonly loggerFactory: LoggerFactory,
    private readonly campaignService: CampaignService,
    private readonly flyoutService: FlyoutService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private readonly snackBarService: SnackBarService,
    private readonly router: Router,
  ) {
    this.logger = loggerFactory.get('FlyoutReferralComponent');
  }

  ngOnInit() {
    this.getTemplates();
  }

  public getTemplates(): void {
    if (this._openingData?.templates) {
      this.setTemplates(this._openingData.templates);
      return;
    }
    this.loadingWidgetTemplates = true;
    this.widgetService.getAllWidgetTemplates().subscribe(
      (templates: Array<CampaignTemplate>) => {
        if (templates) {
          this.setTemplates(templates);
        }
        this.loadingWidgetTemplates = false;
      },
      (error) => {
        this.logger.error('Error that should break the page: ' + JSON.stringify(error));
        this.loadingWidgetTemplates = false;
      },
    );
  }

  private setTemplates(templates: Array<CampaignTemplate>): void {
    const publishedTemplates = templates
      .filter((template) => template.status === TemplateStatus.Published)
      .sort((a, b) => a.order - b.order);
    this.referringTemplates = publishedTemplates.filter(
      (template) => CampaignTemplatesService.getReferralType(template) === ReferralTypes.Referring,
    );
    this.friendsTemplates = publishedTemplates.filter(
      (template) => CampaignTemplatesService.getReferralType(template) === ReferralTypes.Friends,
    );
  }

  public setAccordionStep(value): void {
    this.accordionStep = value;
  }

  public selectReferringTemplate(widgetTemplateId: number): void {
    if (this.creatingPair || this.isReferringPreselected() || !this.disabledFriends() || !this.validateCampaignName()) {
      return;
    }
    this.selectedReferringTemplateId.set(widgetTemplateId);
    if (this.preselectedFriendTemplateId()) {
      this.createPair(widgetTemplateId, this.preselectedFriendTemplateId());
      return;
    }
    this.accordionStep = ReferralTypes.Friends;
  }

  public selectFriendTemplate(widgetTemplateId: number): void {
    if (this.creatingPair || this.preselectedFriendTemplateId() || !this.validateCampaignName()) {
      return;
    }
    if (!this.disabledFriends()) {
      this.selectedFriendTemplateId.set(widgetTemplateId);
      this.runCreation(this.addFriend(this._openingData.campaignReferral, widgetTemplateId));
    } else if (this.selectedReferringTemplateId()) {
      this.selectedFriendTemplateId.set(widgetTemplateId);
      this.createPair(this.selectedReferringTemplateId(), widgetTemplateId);
    }
  }

  private createPair(referringTemplateId: number, friendTemplateId: number): void {
    const name = this.referralName();
    this.runCreation(
      this.createCampaign(referringTemplateId).pipe(
        switchMap((referrer) => {
          this.createdIds.referrer = referrer.id;
          return this.campaignService.createReferralCampaign(referrer.id, name).pipe(
            switchMap((group) => {
              this.createdIds.group = group.id;
              return this.saveCampaignName(referrer, `${name} (Referrer)`).pipe(map(() => group));
            }),
          );
        }),
        switchMap((group) => this.addFriend(group, friendTemplateId)),
      ),
    );
  }

  private addFriend(group: OnsiteCampaign, friendTemplateId: number): Observable<OnsiteCampaign> {
    const friendName = `${this.referralName()} (${this.translateService.instant('referral-campaign.friend-in-name-campaign')})`;
    return this.createCampaign(friendTemplateId).pipe(
      switchMap((friend) => {
        this.createdIds.friend = friend.id;
        return this.campaignService
          .updateReferralCampaign({ ...group, friendCampaign: { id: friend.id } })
          .pipe(
            switchMap((linkedGroup) =>
              this.saveCampaignName(friend, friendName).pipe(map(() => ({ ...linkedGroup, name: friendName }))),
            ),
          );
      }),
    );
  }

  private createCampaign(widgetTemplateId: number): Observable<OnsiteCampaign> {
    const account = this._openingData.currentAccount;
    return this.campaignService
      .createOnsiteCampaignFromTemplate(
        widgetTemplateId,
        OnsiteCampaignType.Regular,
        account.defaultFontFamily,
        OnsiteCampaignType.Regular,
        null,
        account.isPoweredByDisabled,
        account.isAttributionActivated,
      )
      .pipe(
        map((campaign: OnsiteCampaign) => {
          if (!campaign) {
            throw new Error('No campaign was returned');
          }
          return campaign;
        }),
      );
  }

  // A referral is saved whole or not at all: on any failure, whatever this run created is deleted again.
  private runCreation(creation: Observable<OnsiteCampaign>): void {
    this.creatingPair = true;
    this.createdIds = {};
    this.showCampaignProgressDialog();
    creation.subscribe({
      next: (referral) => {
        this.flyoutService.flyoutUpdatedReferralBS.next(referral);
        // Opened from a template: the referrer is set up first, whichever half was picked there.
        if (this._openingData.preselectedTemplate) {
          this.openEditor(this.createdIds.referrer);
          return;
        }
        this.closeCampaignProgressDialog();
        this.cancelFlyout();
      },
      error: (error) => {
        this.logger.error('Could not create the referral campaigns: ' + JSON.stringify(error));
        this.rollback().subscribe({
          complete: () => {
            this.creatingPair = false;
            this.closeCampaignProgressDialog();
            this.snackBarService.error('referral-campaign.create-failed');
          },
        });
      },
    });
  }

  // The loader stays up until the editor is reached, so the page never unlocks in between.
  private openEditor(campaignId: number): void {
    const finish = () => {
      this.closeCampaignProgressDialog();
      this.cancelFlyout();
    };
    this.widgetService.getWidgetsByCampaignId(campaignId).subscribe({
      next: (widgets: Array<Widget>) => {
        if (!widgets?.length) {
          this.logger.error(`New referral campaign ${campaignId} has no widget to open`);
          finish();
          return;
        }
        this.router
          .navigate([`/campaigns/${campaignId}/widgets/${widgets[0].id}/editor/`], {
            queryParams: { isNewWidget: true },
          })
          .finally(finish);
      },
      error: (error) => {
        this.logger.error('Could not open the new referral campaign: ' + JSON.stringify(error));
        finish();
      },
    });
  }

  // Deleting the group deletes its referrer too; the friend is never linked yet, as linking is the last step that can fail.
  private rollback(): Observable<unknown> {
    const { group, referrer, friend } = this.createdIds;
    const logError = (error: unknown) =>
      this.logger.error('Could not delete a half-created referral campaign: ' + JSON.stringify(error));
    const deleteCampaign = (id: number) =>
      this.campaignService.deleteCampaign(id).pipe(
        catchError((error) => {
          logError(error);
          return EMPTY;
        }),
      );
    const groupDeleted$ = group
      ? this.campaignService.deleteReferralCampaign(group).pipe(
          map(() => true),
          catchError((error) => {
            logError(error);
            return of(false);
          }),
        )
      : of(false);
    return groupDeleted$.pipe(
      switchMap((groupDeleted) =>
        concat(referrer && !groupDeleted ? deleteCampaign(referrer) : EMPTY, friend ? deleteCampaign(friend) : EMPTY),
      ),
    );
  }

  public cancelFlyout(): void {
    this.flyoutService.closeReferralFlyout(undefined);
  }

  // One dialog covers the whole referrer-group-friend chain, so the page never unlocks mid-way.
  private showCampaignProgressDialog(): void {
    if (this.progressDialogRef) {
      return;
    }
    this.progressDialogRef = this.dialog.open(IndeterminateProgressDialogComponent, {
      panelClass: 'transparent',
      disableClose: true,
    });
  }

  private closeCampaignProgressDialog(): void {
    this.progressDialogRef?.close();
    this.progressDialogRef = undefined;
  }

  public validateCampaignName(): boolean {
    const namePattern = /^([0-9A-Za-z�����Œ'()\\?!&\/%;:+<>=\-_\s\.])+$/;
    if (!this.referralName()) {
      this.campaignNameValidationError.set('campaign.settings.basic.campaign-name-required');
      return false;
    }
    if (!namePattern.test(this.nameReferral())) {
      this.campaignNameValidationError.set('campaign.settings.basic.campaign-name-pattern');
      return false;
    }
    if (this.referralName().length > 100) {
      this.campaignNameValidationError.set('campaign.settings.basic.campaign-name-maxlength');
      return false;
    }
    this.campaignNameValidationError.set(undefined);
    return true;
  }

  // Completes even when the rename fails, so callers report the half after it and listeners never reload too early.
  public saveCampaignName(campaign: OnsiteCampaign, name: string): Observable<unknown> {
    return this.campaignService
      .updateCampaignWithPatch(
        campaign,
        this.campaignService.getPatchOperations({ id: campaign.id, name: name } as OnsiteCampaign),
      )
      .pipe(
        catchError((error) => {
          this.logger.error(`Could not save campaign name: ${error}`);
          return of(undefined);
        }),
      );
  }
}
